import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Paperclip, X, Loader2, Upload } from "lucide-react";
import { post } from "@/lib/apiClient";
import { API_URL, STORAGE } from "@/_config";
import { toast } from "sonner";
import SupportEmailEditor from "@/components/support/SupportEmailEditor";

interface Attachment {
  url: string;
  name: string;
}

export default function CreateSupportTicketPage() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submittedTicket, setSubmittedTicket] = useState<{ ticket_number: string | null; subject: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const MAX_ATTACHMENTS = 5;

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} attachments allowed.`);
      return;
    }

    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const raw = localStorage.getItem(STORAGE);
      const stored = raw ? JSON.parse(raw) : null;
      const token = stored?.token ?? stored?.access_token ?? "";

      const uploaded: Attachment[] = [];
      for (const file of toUpload) {
        const form = new FormData();
        form.append("image", file);
        const res = await fetch(API_URL.EMAIL_IMAGES, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        if (!res.ok) {
          toast.error(`Failed to upload ${file.name}.`);
          continue;
        }
        const data = await res.json();
        const url = data?.data?.url;
        if (url) uploaded.push({ url, name: file.name });
      }
      setAttachments((prev) => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
      // Reset file input so the same file can be selected again
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!subject.trim()) errs.subject = "Subject is required.";
    if (!body.trim() || body === "<p></p>") errs.body = "Message body is required.";
    return errs;
  };

  const queryClient = useQueryClient();

  const { mutate: createTicket, isPending } = useMutation({
    mutationFn: () =>
      post(API_URL.SUPPORT.TICKETS, {
        subject: subject.trim(),
        body,
        attachments: attachments.map((a) => a.url),
      }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      const data = res?.data ?? res;
      setSubmittedTicket({
        ticket_number: data?.ticket_number ?? null,
        subject: data?.subject ?? subject.trim(),
      });
    },
    onError: (err: any) => {
      toast.error(err?.message ?? "Failed to submit support request. Please try again.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    createTicket();
  };

  if (submittedTicket) {
    return (
      <div style={{ maxWidth: 560, margin: "80px auto", padding: "0 24px", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>
          Request Submitted
        </h1>
        <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6, marginBottom: 8 }}>
          Your support request has been received. We'll get back to you as soon as possible.
        </p>
        {submittedTicket.ticket_number && (
          <div style={{
            background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10,
            padding: "14px 20px", margin: "20px auto", display: "inline-block",
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0284c7", textTransform: "uppercase" as const, letterSpacing: "0.5px" }}>
              Your Ticket Number
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#0284c7", fontFamily: "monospace" }}>
              {submittedTicket.ticket_number}
            </div>
          </div>
        )}
        <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 24 }}>
          A confirmation email has been sent to you with your ticket details.
        </p>
        <button
          onClick={() => navigate("/app/support")}
          style={{
            background: "#0284c7", color: "#fff", border: "none",
            borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}
        >
          View My Requests
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
        <button
          onClick={() => navigate("/app/support")}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
            padding: "7px 12px", fontSize: 13, color: "#374151", cursor: "pointer",
          }}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#1e293b" }}>
            Create Support Request
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 13, color: "#64748b" }}>
            Describe your issue and we'll respond as soon as possible.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 28, display: "flex", flexDirection: "column" as const, gap: 20 }}>
          {/* Subject */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              Subject <span style={{ color: "#e53e3e" }}>*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => { setSubject(e.target.value); if (errors.subject) setErrors((p) => ({ ...p, subject: "" })); }}
              placeholder="Brief description of your issue"
              maxLength={255}
              style={{
                width: "100%", boxSizing: "border-box" as const, padding: "9px 12px",
                fontSize: 14, border: `1px solid ${errors.subject ? "#e53e3e" : "#e2e8f0"}`,
                borderRadius: 8, outline: "none", color: "#1e293b",
              }}
            />
            {errors.subject && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#e53e3e" }}>{errors.subject}</p>}
          </div>

          {/* Body */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              Message <span style={{ color: "#e53e3e" }}>*</span>
            </label>
            <SupportEmailEditor
              value={body}
              onChange={(html) => { setBody(html); if (errors.body) setErrors((p) => ({ ...p, body: "" })); }}
              hasError={!!errors.body}
            />
            {errors.body && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#e53e3e" }}>{errors.body}</p>}
          </div>

          {/* Attachments */}
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              Attachments <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>(optional, max {MAX_ATTACHMENTS} images)</span>
            </label>

            {attachments.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginBottom: 10 }}>
                {attachments.map((att, idx) => (
                  <div key={idx} style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: "#f1f5f9", borderRadius: 6, padding: "5px 8px 5px 10px",
                    fontSize: 12, color: "#374151", maxWidth: 200,
                  }}>
                    <Paperclip size={12} style={{ flexShrink: 0 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>
                      {att.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(idx)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: "#94a3b8" }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {attachments.length < MAX_ATTACHMENTS && (
              <>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 14px", border: "1px dashed #cbd5e1", borderRadius: 8,
                    background: "#f8fafc", color: "#64748b", fontSize: 13, cursor: uploading ? "not-allowed" : "pointer",
                    opacity: uploading ? 0.6 : 1,
                  }}
                >
                  {uploading ? <Loader2 size={14} style={{ animation: "spin 0.8s linear infinite" }} /> : <Upload size={14} />}
                  {uploading ? "Uploading…" : "Add Image"}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleAttachmentUpload}
                />
              </>
            )}
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
          <button
            type="button"
            onClick={() => navigate("/app/support")}
            style={{
              padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8,
              background: "#fff", color: "#374151", fontSize: 14, cursor: "pointer", fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "9px 22px", border: "none", borderRadius: 8,
              background: isPending ? "#93c5fd" : "#0284c7",
              color: "#fff", fontSize: 14, fontWeight: 600,
              cursor: isPending ? "not-allowed" : "pointer",
            }}
          >
            {isPending && <Loader2 size={14} style={{ animation: "spin 0.8s linear infinite" }} />}
            {isPending ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </form>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
